<template>
<div class="fp-root" :style="themePageStyle">

  <nav class="fp-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Farmer Forum</span>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/learn')"><i class="mdi mdi-book-open-variant me-1"></i>Learning Hub</button>
      <button class="btn btn-sm btn-warning" @click="newTopic"><i class="mdi mdi-plus me-1"></i>New Topic</button>
    </div>
  </div></nav>

  <div class="container fp-container">

    <!-- Hero -->
    <div class="text-center mb-4">
      <h4 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">💬 Farmer Forum</h4>
      <p style="color:var(--fm-s,#6B6356);font-size:13px;max-width:640px;margin:0 auto;">
        Ask questions, share experiences, and learn from fellow farmers and verified vets.
      </p>
    </div>

    <!-- Filters + search -->
    <div class="d-flex gap-2 mb-3 flex-wrap align-items-center">
      <div class="input-group" style="max-width:340px;">
        <span class="input-group-text fp-addon"><i class="mdi mdi-magnify"></i></span>
        <input v-model="search" class="form-control fp-input" placeholder="Search topics..." @keyup.enter="loadTopics" />
      </div>
      <select v-model="filter.category" class="form-select fp-input" style="max-width:200px;" @change="loadTopics">
        <option value="">All categories</option>
        <option value="general">General</option>
        <option value="health">Health</option>
        <option value="feeding">Feeding</option>
        <option value="breeding">Breeding</option>
        <option value="marketplace">Marketplace</option>
        <option value="equipment">Equipment</option>
        <option value="other">Other</option>
      </select>
      <button class="fp-chip" :class="{active: filter.unansweredOnly}" @click="filter.unansweredOnly=!filter.unansweredOnly; loadTopics()">
        <i class="mdi mdi-help-circle-outline me-1"></i>Unanswered only
      </button>
    </div>

    <!-- Topics list -->
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="!topics.length" class="text-center py-5">
      <i class="mdi mdi-forum-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No topics yet</h5>
      <p style="color:var(--fm-s,#6B6356);">Be the first to start a discussion.</p>
      <button class="btn btn-warning" @click="newTopic"><i class="mdi mdi-plus me-1"></i>Start a Topic</button>
    </div>
    <div v-else>
      <div v-for="t in topics" :key="t.id" class="fp-topic" :class="{pinned: t.is_pinned == 1}" @click="$router.push('/forum/'+t.id)">
        <user-avatar :src="t.author_avatar" :name="t.author_name" size="md" />
        <div class="flex-fill" style="min-width:0;">
          <div class="fp-topic-title">
            <i v-if="t.is_pinned == 1" class="mdi mdi-pin me-1" style="color:#BFA164;"></i>
            <i v-else-if="t.is_answered == 1" class="mdi mdi-check-decagram me-1" style="color:#50C878;"></i>
            {{ t.title }}
            <span v-if="t.status === 'closed'" class="fp-st fp-st-closed">closed</span>
          </div>
          <div class="fp-topic-meta">
            <span class="fp-topic-cat">{{ t.category }}</span>
            <span v-if="t.species_name">· {{ t.species_name }}</span>
            <span>· {{ t.author_name && t.author_name.trim() ? t.author_name : 'a farmer' }}</span>
            <span v-if="t.location_label" style="color:#50C878;">· <i class="mdi mdi-map-marker"></i>{{ t.location_label }}</span>
            <span>· {{ relTime(t.created_at) }}</span>
          </div>
        </div>
        <div class="fp-topic-stats">
          <div class="fp-stat"><i class="mdi mdi-message-reply-text-outline me-1"></i>{{ t.reply_count }}</div>
          <div class="fp-stat"><i class="mdi mdi-eye-outline me-1"></i>{{ t.view_count }}</div>
        </div>
      </div>
    </div>

  </div>

  <!-- New topic modal -->
  <div v-if="showNewModal" class="fp-mod-overlay" @click.self="showNewModal=false">
    <div class="fp-mod-card">
      <h5 style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-plus me-2" style="color:#BFA164;"></i>Start a New Topic</h5>
      <label class="fp-lbl">Title <span style="color:#FF6B6B;">*</span></label>
      <input v-model="newForm.title" class="form-control fp-input" placeholder="e.g., Help — my goats are off feed" />
      <label class="fp-lbl">Category</label>
      <select v-model="newForm.category" class="form-select fp-input">
        <option value="general">General</option>
        <option value="health">Health</option>
        <option value="feeding">Feeding</option>
        <option value="breeding">Breeding</option>
        <option value="marketplace">Marketplace</option>
        <option value="equipment">Equipment</option>
        <option value="other">Other</option>
      </select>
      <label class="fp-lbl">Question / details <span style="color:#FF6B6B;">*</span></label>
      <textarea v-model="newForm.content" class="form-control fp-input" rows="5" placeholder="Describe what's going on, what you've tried, what you need help with..."></textarea>

      <!-- Media -->
      <label class="fp-lbl">Photos, video &amp; documents <span style="color:var(--fm-s,#6B6356);font-size:10px;font-weight:400;">(optional, up to 6 — photos auto-compressed)</span></label>
      <div class="fp-media-row">
        <template v-for="(m, mi) in newForm.media" :key="mi">
          <!-- Image / video thumb -->
          <div v-if="m.type !== 'document'" class="fp-media-thumb">
            <img v-if="m.type === 'image'" :src="m.preview" />
            <video v-else :src="m.preview" muted></video>
            <button class="fp-media-remove" @click="removeMedia('new', mi)" type="button" title="Remove">
              <i class="mdi mdi-close"></i>
            </button>
            <span v-if="m.type === 'video'" class="fp-media-badge"><i class="mdi mdi-video"></i></span>
          </div>
          <!-- Document chip -->
          <div v-else class="fp-doc-chip">
            <i class="mdi" :class="docIcon(m.name)" style="font-size:22px;color:#BFA164;"></i>
            <div class="fp-doc-info">
              <div class="fp-doc-name">{{ m.name || m.file.name }}</div>
              <div class="fp-doc-size">{{ humanSize(m.file ? m.file.size : 0) }}</div>
            </div>
            <button class="fp-doc-remove" @click="removeMedia('new', mi)" type="button" title="Remove">
              <i class="mdi mdi-close"></i>
            </button>
          </div>
        </template>
        <label v-if="newForm.media.length < 6 && !newForm.compressing" class="fp-media-add">
          <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" multiple @change="onMediaPick($event, 'new')" />
          <i class="mdi mdi-paperclip" style="font-size:24px;"></i>
          <span>Add file</span>
        </label>
        <div v-if="newForm.compressing" class="fp-media-add" style="pointer-events:none;">
          <span class="spinner-border text-warning"></span>
          <span style="margin-top:6px;">Compressing...</span>
        </div>
      </div>

      <!-- Location -->
      <div class="fp-loc-row">
        <label class="fp-loc-label">
          <input type="checkbox" v-model="newForm.shareLocation" @change="onLocationToggle('new')" />
          <i class="mdi mdi-map-marker me-1" style="color:#50C878;"></i>
          <span v-if="newForm.locationLabel">Posting from <strong style="color:#50C878;">{{ newForm.locationLabel }}</strong></span>
          <span v-else-if="newForm.locationBusy"><span class="spinner-border spinner-border-sm me-1"></span>Detecting your location...</span>
          <span v-else>Share my location with this post</span>
        </label>
        <div v-if="newForm.locationError" style="font-size:11px;color:#FF6B6B;margin-top:4px;">{{ newForm.locationError }}</div>
      </div>

      <div v-if="newError" class="text-danger" style="font-size:12px;margin-top:6px;">{{ newError }}</div>
      <div class="d-flex gap-2 mt-3">
        <button class="btn btn-outline-secondary flex-fill" @click="showNewModal=false" :disabled="newBusy">Cancel</button>
        <button class="btn btn-warning flex-fill" @click="submitTopic" :disabled="newBusy || !newForm.title.trim() || !newForm.content.trim()">
          <span v-if="newBusy" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="mdi mdi-send me-1"></i>Post Topic
        </button>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important}
html[data-theme="light"] .fp-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .fp-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .fp-topic,
html[data-theme="light"] .fp-mod-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .fp-input,
html[data-theme="light"] .fp-addon,
html[data-theme="light"] .fp-chip{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#555!important}
</style>

<style scoped>
.fp-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.fp-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.fp-container{padding-top:80px;padding-bottom:60px;max-width:900px}
.fp-input{background:var(--fm-bg1,#141414)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.fp-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}
.fp-addon{background:var(--fm-bg2,#0F0F0F)!important;border-color:var(--fm-br,#1E1E1E)!important;color:var(--fm-s,#6B6356)!important}
.fp-chip{padding:6px 14px;border-radius:20px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg1,#141414);color:var(--fm-m,#A09888);font-size:12px;cursor:pointer;transition:all .2s}
.fp-chip.active{background:rgba(191,161,100,.1);border-color:#BFA164;color:#BFA164;font-weight:600}

.fp-topic{display:flex;align-items:center;gap:12px;padding:14px 16px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;margin-bottom:8px;cursor:pointer;transition:all .15s}
.fp-topic:hover{border-color:#BFA164;transform:translateX(2px)}
.fp-topic.pinned{border-left:3px solid #BFA164}
.fp-topic-icon{width:38px;height:38px;border-radius:8px;background:rgba(191,161,100,.1);color:#BFA164;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.fp-topic-title{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:14px;line-height:1.4}
.fp-topic-meta{color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px}
.fp-topic-cat{display:inline-block;text-transform:capitalize;color:#BFA164;font-weight:600}
.fp-topic-stats{display:flex;gap:14px;font-size:11px;color:var(--fm-m,#A09888);flex-shrink:0}
.fp-stat{text-align:center}

.fp-st{font-size:9px;padding:1px 6px;border-radius:6px;margin-left:6px;text-transform:uppercase;letter-spacing:.3px;font-weight:600}
.fp-st-closed{background:rgba(255,107,107,.1);color:#FF6B6B}

.fp-mod-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);backdrop-filter:blur(4px);z-index:2000;display:flex;align-items:center;justify-content:center;padding:20px}
.fp-mod-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:22px;max-width:520px;width:100%;max-height:90vh;overflow-y:auto}
.fp-lbl{display:block;color:var(--fm-h,#F5F0E8);font-size:12px;font-weight:600;margin:10px 0 4px}

.fp-media-row{display:flex;flex-wrap:wrap;gap:8px;margin-top:6px}
.fp-media-thumb{position:relative;width:84px;height:84px;border-radius:8px;overflow:hidden;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E)}
.fp-media-thumb img,.fp-media-thumb video{width:100%;height:100%;object-fit:cover}
.fp-media-remove{position:absolute;top:2px;right:2px;width:22px;height:22px;border-radius:50%;background:rgba(0,0,0,.7);border:none;color:#FF6B6B;font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.fp-media-badge{position:absolute;bottom:4px;left:4px;background:rgba(0,0,0,.7);color:#FFD700;font-size:10px;padding:1px 6px;border-radius:6px;display:inline-flex;align-items:center;gap:2px}
.fp-media-add{width:84px;height:84px;border-radius:8px;border:2px dashed var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F);display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;color:var(--fm-s,#6B6356);font-size:9px;text-align:center;padding:4px}
.fp-media-add:hover{border-color:#BFA164;color:#BFA164}
.fp-media-add input{display:none}

.fp-doc-chip{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F);min-width:160px;max-width:240px;height:84px;position:relative}
.fp-doc-info{flex:1;min-width:0}
.fp-doc-name{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:11px;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fp-doc-size{color:var(--fm-s,#6B6356);font-size:10px}
.fp-doc-remove{position:absolute;top:4px;right:4px;width:20px;height:20px;border-radius:50%;background:rgba(0,0,0,.4);border:none;color:#FF6B6B;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center}

.fp-loc-row{margin-top:14px;padding:10px 12px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px}
.fp-loc-label{display:flex;align-items:center;gap:8px;cursor:pointer;font-size:12px;color:var(--fm-m,#A09888);margin:0;line-height:1.4}
.fp-loc-label input[type="checkbox"]{accent-color:#50C878;width:16px;height:16px;cursor:pointer}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

// Shared image compressor — same canvas pattern as CreateListingPage (1200px max, JPEG ~0.8,
// re-encodes to ~0.6 if first pass is still over 2MB). Returns a File and a preview URL.
function compressImage(file, maxDim, quality) {
  return new Promise(function(resolve){
    var reader = new FileReader();
    reader.onload = function(e){
      var img = new Image();
      img.onload = function(){
        var w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
          else       { w = Math.round(w * maxDim / h); h = maxDim; }
        }
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob(function(blob){
          if (!blob) { resolve({ file: file, preview: e.target.result }); return; }
          var emit = function(b){
            var name = file.name.replace(/\.[^.]+$/, '.jpg');
            var f = new File([b], name, { type: 'image/jpeg' });
            resolve({ file: f, preview: URL.createObjectURL(f) });
          };
          if (blob.size > 2 * 1024 * 1024) {
            canvas.toBlob(function(b2){ emit(b2 || blob); }, 'image/jpeg', 0.6);
          } else { emit(blob); }
        }, 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default {
  name: "ForumPage",
  components: { UserAvatar },
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:false,search:'',
    filter: { category:'', unansweredOnly:false },
    topics: [],
    showNewModal: false,
    newForm: { title:'', category:'general', content:'', media: [], compressing: false, shareLocation: false, locationLabel:'', locationLat:null, locationLng:null, locationBusy:false, locationError:'' },
    newBusy: false, newError: ''
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'}
  },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.loadTopics();
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    relTime(ts){
      if(!ts) return '';
      var diff = Math.floor((Date.now() - new Date(ts.replace(' ','T')).getTime()) / 1000);
      if (diff < 60) return 'just now';
      if (diff < 3600) return Math.floor(diff/60) + 'm ago';
      if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
      if (diff < 2592000) return Math.floor(diff/86400) + 'd ago';
      return ts.split(' ')[0];
    },
    async loadTopics(){
      this.loading=true;
      try{
        var params={function:'getForumTopics',limit:50};
        if(this.filter.category) params.category=this.filter.category;
        if(this.filter.unansweredOnly) params.unansweredOnly=1;
        if(this.search) params.search=this.search;
        var r=await executeJson(params,flocker);
        if(r.data&&r.data.success) this.topics=(r.data.data&&r.data.data.topics)||[];
      }catch(e){console.error(e)}
      this.loading=false;
    },
    newTopic(){
      if(!sessionStorage.getItem('userId')){this.$router.push('/login?redirect=/forum');return}
      this.newForm={title:'',category:'general',content:'',media:[],compressing:false,shareLocation:false,locationLabel:'',locationLat:null,locationLng:null,locationBusy:false,locationError:''};
      this.newError='';
      this.showNewModal=true;
    },
    onLocationToggle(target){
      var form = target === 'new' ? this.newForm : null;
      if (!form) return;
      if (!form.shareLocation) {
        form.locationLabel=''; form.locationLat=null; form.locationLng=null; form.locationError='';
        return;
      }
      if (!navigator.geolocation) {
        form.shareLocation=false;
        form.locationError = 'Geolocation not supported on this browser';
        return;
      }
      form.locationBusy = true;
      form.locationError = '';
      navigator.geolocation.getCurrentPosition(async function(pos){
        form.locationLat = pos.coords.latitude;
        form.locationLng = pos.coords.longitude;
        // Reverse-geocode via Nominatim — same pattern used by listing/transport pages.
        try {
          var url = 'https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=' + form.locationLat + '&lon=' + form.locationLng + '&zoom=8';
          var resp = await fetch(url, { headers: { 'Accept-Language': 'en' } });
          var data = await resp.json();
          var a = data.address || {};
          // Compose a concise label: county/region, country
          var parts = [a.city || a.town || a.village || a.county || a.state_district || a.state, a.country];
          form.locationLabel = parts.filter(Boolean).join(', ') || data.display_name || ('GPS ' + form.locationLat.toFixed(2) + ', ' + form.locationLng.toFixed(2));
        } catch(e) {
          form.locationLabel = 'GPS ' + form.locationLat.toFixed(2) + ', ' + form.locationLng.toFixed(2);
        }
        form.locationBusy = false;
      }, function(err){
        form.locationBusy = false;
        form.shareLocation = false;
        form.locationError = err && err.code === 1 ? 'Permission denied' : 'Could not detect location';
      }, { timeout: 8000, enableHighAccuracy: false });
    },
    async onMediaPick(event, target){
      var self=this;
      var form = target === 'new' ? this.newForm : null;
      if (!form) return;
      var files = Array.from(event.target.files || []);
      var slots = 6 - form.media.length;
      if (slots <= 0) { event.target.value=''; return; }
      var picked = files.slice(0, slots);
      form.compressing = true;
      try {
        for (var i = 0; i < picked.length; i++) {
          var f = picked[i];
          if (f.type.indexOf('image/') === 0) {
            var c = await compressImage(f, 1200, 0.8);
            form.media.push({ type:'image', file: c.file, preview: c.preview });
          } else if (f.type.indexOf('video/') === 0) {
            // Hard cap on video size — 25MB. Real compression would need ffmpeg.wasm.
            if (f.size > 25 * 1024 * 1024) {
              this.newError = 'Videos must be under 25 MB. Trim it on your phone first.';
              continue;
            }
            form.media.push({ type:'video', file: f, preview: URL.createObjectURL(f) });
          } else if (this.isDocFile(f)) {
            // Documents: PDF, Word, Excel, PowerPoint, plain text, CSV — capped at 10MB
            if (f.size > 10 * 1024 * 1024) {
              this.newError = 'Documents must be under 10 MB.';
              continue;
            }
            form.media.push({ type:'document', file: f, name: f.name, preview: '' });
          } else {
            this.newError = 'Unsupported file type: ' + (f.name || 'file');
          }
        }
      } catch(e){ this.newError = 'Could not process file: ' + (e && e.message); }
      form.compressing = false;
      event.target.value = '';
    },
    isDocFile(f){
      var allowedMime = ['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','application/vnd.ms-powerpoint','application/vnd.openxmlformats-officedocument.presentationml.presentation','text/plain','text/csv'];
      var allowedExt = /\.(pdf|docx?|xlsx?|pptx?|txt|csv)$/i;
      return allowedMime.indexOf(f.type) > -1 || allowedExt.test(f.name || '');
    },
    docIcon(name){
      var n = (name || '').toLowerCase();
      if (n.indexOf('.pdf') > -1) return 'mdi-file-pdf-box';
      if (/\.docx?$/.test(n)) return 'mdi-file-word-box';
      if (/\.xlsx?$/.test(n) || n.indexOf('.csv') > -1) return 'mdi-file-excel-box';
      if (/\.pptx?$/.test(n)) return 'mdi-file-powerpoint-box';
      if (n.indexOf('.txt') > -1) return 'mdi-file-document-outline';
      return 'mdi-file-outline';
    },
    humanSize(b){
      b = Number(b||0); if (b < 1024) return b + ' B';
      if (b < 1024*1024) return (b/1024).toFixed(1) + ' KB';
      return (b/1024/1024).toFixed(1) + ' MB';
    },
    removeMedia(target, idx){
      var form = target === 'new' ? this.newForm : null;
      if (!form) return;
      var m = form.media[idx];
      if (m && m.preview && m.preview.indexOf('blob:') === 0) URL.revokeObjectURL(m.preview);
      form.media.splice(idx, 1);
    },
    async submitTopic(){
      this.newBusy=true; this.newError='';
      try{
        var fd = new FormData();
        fd.append('function', 'createForumTopic');
        fd.append('userId', sessionStorage.getItem('userId'));
        fd.append('title', this.newForm.title);
        fd.append('content', this.newForm.content);
        fd.append('category', this.newForm.category);
        if (this.newForm.shareLocation && this.newForm.locationLat) {
          fd.append('locationLabel', this.newForm.locationLabel || '');
          fd.append('locationLat', String(this.newForm.locationLat));
          fd.append('locationLng', String(this.newForm.locationLng));
        }
        var pi = 0;
        this.newForm.media.forEach(function(m){
          if (m.file) { fd.append('photo' + pi, m.file); pi++; }
        });
        var r = await execute(fd, flocker);
        if(r.data&&r.data.success){
          this.showNewModal=false;
          var newId = r.data.data && r.data.data.topicId;
          if(newId) this.$router.push('/forum/'+newId);
          else this.loadTopics();
        }else{
          this.newError=(r.data&&r.data.message)||'Could not post topic';
        }
      }catch(e){
        this.newError=(e&&e.message)||'Network error';
      }
      this.newBusy=false;
    }
  }
};
</script>
